import { useState, Fragment } from "react";
import { useStore } from "zustand";
import PropTypes from "prop-types";
import {
  Heading,
  ActionButtonGroup,
  ActionButton,
  Divider,
  Text,
  Link,
  Avatar,
  ComboBox,
  ComboBoxItem,
  TextField,
  TextArea,
} from "@react-spectrum/s2";
import EditIcon from "@react-spectrum/s2/icons/Edit";

import { store } from "@app/store";
import {
  GRID_TYPE,
  getCellLink,
  getGroupGrids,
  getPreviewLinks,
  isTextCell,
} from "./wizardSteps";
import { CampaignGridHeader, CampaignFieldRows } from "./CampaignPreview";

import { style } from "@react-spectrum/s2/style" with { type: "macro" };

const formatter = new Intl.ListFormat("en", {
  style: "long",
  type: "conjunction",
});

const EditButton = ({ value, onPress }) => {
  const colorScheme = useStore(store, (state) => state.colorScheme);

  const setPromptHint = useStore(store, (state) => state.setPromptHint);

  const handlePress =
    onPress ??
    (() => {
      setPromptHint(
        `Edit ${formatter.format((Array.isArray(value) ? value : [value]).map((v) => `"${v}"`))}: `,
      );
    });

  return (
    <ActionButton
      aria-label="Edit"
      isQuiet
      size="XS"
      styles={style({
        marginStart: 8,
      })}
      UNSAFE_style={{
        display: "inline-grid",
        verticalAlign: "text-bottom",
      }}
      onPress={handlePress}
    >
      <EditIcon
        UNSAFE_className={style({
          color: {
            colorScheme: {
              dark: "white/25",
              light: "black/25",
            },
          },
        })({ colorScheme })}
      />
    </ActionButton>
  );
};

EditButton.propTypes = {
  value: PropTypes.string.isRequired,
  onPress: PropTypes.func,
};

const fieldWrapperStyle = style({
  marginY: {
    size: {
      S: 4,
      M: 4,
      L: 12,
      XL: 16,
      XXL: 16,
      XXXL: 16,
    },
  },
});

const labelStyle = style({
  font: {
    size: {
      S: "detail-sm",
      M: "detail-sm",
      L: "detail",
      XL: "detail-lg",
      XXL: "detail-lg",
      XXXL: "detail-lg",
    },
  },
});

const CampaignField = ({
  selectedCampaign = {},
  selectedPersona = {},
  label,
  value,
  size = "L",
  isDisabled,
  canEditCampaign = true,
  type,
  values = [],
  onFieldChange,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const fieldKey = Array.isArray(value) ? value[0] : value;
  const [_fieldValue, setFieldValue] = useState(null);
  const fieldValue = _fieldValue ?? selectedCampaign?.[fieldKey] ?? "";

  const TextFieldComponent = selectedCampaign?.[value]?.includes("\n")
    ? TextArea
    : TextField;

  const handleFieldChange = () => {
    onFieldChange?.(fieldKey, fieldValue);
    setIsEditing(false);
  };

  if (type === "dropdown") {
    const dropdownItems = values.map((option) => ({
      id: option,
      label: option,
    }));

    return (
      <div className={fieldWrapperStyle}>
        <ComboBox
          label={label}
          aria-label={label || fieldKey}
          items={dropdownItems}
          selectedKey={selectedCampaign?.[fieldKey] ?? null}
          onSelectionChange={(key) => {
            if (key != null && onFieldChange) {
              onFieldChange(fieldKey, key);
            }
          }}
          isDisabled={isDisabled}
        >
          {(item) => (
            <ComboBoxItem id={item.id} textValue={item.label}>
              {item.label}
            </ComboBoxItem>
          )}
        </ComboBox>
      </div>
    );
  }

  return (
    <div className={fieldWrapperStyle({ size })}>
      {!!label && <div className={labelStyle({ size })}>{label}</div>}
      {!isEditing && (
        <div
          className={style({
            display: "inline",
            alignItems: "center",
            font: {
              size: {
                S: "detail",
                M: "detail-lg",
                L: "body-lg",
                XL: "body-xl",
                XXL: "body-2xl",
                XXXL: "body-3xl",
              },
            },
            // whiteSpace: "nowrap",
          })({ size })}
        >
          {(Array.isArray(value) ? value : [value]).map((val, idx) => {
            if ((selectedCampaign?.[val] ?? "")?.startsWith("http")) {
              return (
                <Link
                  key={`${val}-${idx}`}
                  variant="primary"
                  href={selectedCampaign?.[val] ?? ""}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {eval(`selectedCampaign["${val}"] ?? ""`)}
                </Link>
              );
            }

            return (
              <Fragment key={`${val}-${idx}`}>
                {val === "Persona-1-name" && !!selectedPersona?.avatarUrl && (
                  <Avatar
                    src={selectedPersona?.avatarUrl}
                    alt="Persona avatar"
                    size={80}
                    isOverBackground
                    styles={style({
                      marginBottom: 12,
                    })}
                  />
                )}
                <Text styles={style({ whiteSpace: "break-spaces" })}>
                  {(selectedCampaign?.[val] ?? "") + " "}
                </Text>
              </Fragment>
            );
          })}
          {!isDisabled && canEditCampaign && (
            <EditButton value={value} onPress={() => setIsEditing(true)} />
          )}
        </div>
      )}
      {isEditing && (
        <div
          className={style({
            display: "flex",
            width: "full",
            gap: 8,
            position: "relative",
          })}
        >
          <TextFieldComponent
            value={fieldValue}
            onChange={setFieldValue}
            autoFocus
            size={size}
            styles={style({ flexGrow: 1 })}
          />
          <ActionButtonGroup
            size="S"
            isQuiet
            styles={style({
              width: "max",
              position: "absolute",
              insetStart: "100%",
              marginStart: 12,
            })}
          >
            <ActionButton onPress={() => handleFieldChange()}>
              Save
            </ActionButton>
            <ActionButton onPress={() => setIsEditing(false)}>
              Cancel
            </ActionButton>
          </ActionButtonGroup>
        </div>
      )}
    </div>
  );
};

CampaignField.propTypes = {
  selectedCampaign: PropTypes.object,
  selectedPersona: PropTypes.object,
  label: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]).isRequired,
  size: PropTypes.oneOf(["S", "M", "L", "XL", "XXL", "XXXL"]),
  isDisabled: PropTypes.bool,
  canEditCampaign: PropTypes.bool,
  type: PropTypes.string,
  values: PropTypes.arrayOf(PropTypes.string),
  onFieldChange: PropTypes.func,
};

export const PartnershipValuePreview = ({
  selectedCampaign = {},
  selectedPersona = {},
  campaignStructure = [],
  templateData,
  canEditCampaign = true,
  onFieldChange,
}) => {
  return (
    <div
      className={style({
        width: "full",
        maxWidth: 640,
        marginX: "auto",
        textWrap: "pretty",
      })}
    >
      {campaignStructure.map((group, idx) => (
        <Fragment key={idx}>
          <Divider size="S" />
          <div
            className={style({
              boxSizing: "border-box",
              width: "full",
              paddingX: 24,
              marginTop: 12,
              marginBottom: 48,
            })}
          >
            <div
              className={style({
                display: "flex",
                gap: 8,
                alignItems: "baseline",
              })}
            >
              <Heading
                level={2}
                styles={style({
                  font: "heading-lg",
                  color: "disabled",
                  // textAlign: "center",
                })}
              >
                {group.title}
              </Heading>
              {!!group.description && (
                <div className={style({ font: "detail", color: "disabled" })}>
                  {group.description}
                </div>
              )}
            </div>
            {getGroupGrids(group)
              .filter((grid) => grid.type !== GRID_TYPE.VARIATIONS)
              .map((grid, gridIdx) => (
                <Fragment key={gridIdx}>
                  <CampaignGridHeader
                    title={grid.title}
                    description={grid.description}
                    previewLinks={getPreviewLinks(
                      grid.previewLinks,
                      templateData?.previewUrl,
                    )}
                    previewPassword={templateData?.previewPassword}
                  />
                  <CampaignFieldRows
                    rows={grid.rows}
                    renderField={(cell) => {
                      const {
                        label,
                        value,
                        size = "L",
                        disabled = false,
                        type,
                        values = [],
                      } = cell;

                      // A link is shown by the visuals preview only.
                      return !isTextCell(cell) && getCellLink(cell) ? null : (
                        <CampaignField
                          selectedCampaign={selectedCampaign}
                          selectedPersona={selectedPersona}
                          label={label}
                          value={value}
                          size={size}
                          isDisabled={disabled}
                          canEditCampaign={canEditCampaign}
                          type={type}
                          values={values}
                          onFieldChange={onFieldChange}
                        />
                      );
                    }}
                  />
                </Fragment>
              ))}
          </div>
        </Fragment>
      ))}
    </div>
  );
};

PartnershipValuePreview.propTypes = {
  selectedCampaign: PropTypes.object,
  selectedPersona: PropTypes.object,
  campaignStructure: PropTypes.array,
  templateData: PropTypes.object,
  canEditCampaign: PropTypes.bool,
  onFieldChange: PropTypes.func,
};
